import "./App.css";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useAppDispatch } from "./app/hook";
import { handleResetAppearance } from "./features/appearance/appearanceSlice";
import { handleResetWizard } from "./features/bot/botSlice";

const getLocaleFromPath = (path: string) => {
  const regex = /\/([a-z]{2}-[A-Z]{2})\/?/i; // Matches two-letter language code followed by optional hyphen and two-letter country code (case-insensitive)
  const match = regex.exec(path);
  return match ? match[1] : null;
};

const lngMapper: { [key: string]: string } = {
  "en-US": "en",
  "fr-FR": "fr"
};

function App({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const currentPersistedLng = i18n.language;

    const pathLanguage = getLocaleFromPath(pathname);

    if (
      pathLanguage &&
      lngMapper[pathLanguage] &&
      lngMapper[pathLanguage] !== currentPersistedLng
    ) {
      i18n.changeLanguage(lngMapper[pathLanguage]);
    }
  }, [pathname]);

  // Manage keyboard opened on mobile devices
  useEffect(() => {
    const handleKeyboardShow = (event: any) => {
      // Adjust viewport based on event details
      const keyboardHeight = event.detail.keyboardHeight;
      document.body.style.paddingBottom = `${keyboardHeight}px`;
    };

    const handleKeyboardHide = () => {
      // Reset viewport to original state
      document.body.style.paddingBottom = "0";
    };

    window.addEventListener("keyboardWillShow", handleKeyboardShow);

    window.addEventListener("keyboardWillHide", handleKeyboardHide);

    return () => {
      window.removeEventListener("keyboardWillShow", handleKeyboardShow);

      window.removeEventListener("keyboardWillHide", handleKeyboardHide);
    };
  }, []);

  // Make sure that bot wizard process and apiAppearance is cleared
  useEffect(() => {
    return () => {
      dispatch(handleResetWizard());
      dispatch(handleResetAppearance());
    };
  }, []);

  return (
    <div className="App">
      <main className="app-content">{children}</main>
    </div>
  );
}

export default App;
